import clsx from "clsx";
import React from "react";
import {
  Checkbox,
  CheckboxProps,
  FormGroup,
  FormControlLabel,
  Popover,
  Typography,
  Select,
  SelectProps,
  useMediaQuery,
  useTheme,
  IconButton,
  Badge,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ChevronDownIcon as ArrowDropDown, Filter } from "lucide-react";

import { ActivityType, activityResponseTypeLabels } from "../../constants";
import { filterInputHeightProps } from "../../components/coach-programs/CoachProgramsFilter";

const useStyles = makeStyles((theme) => ({
  select: {
    backgroundColor: theme.palette.background.paper,
    ...filterInputHeightProps,

    "& [role=combobox]": {
      color: theme.palette.text.secondary,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
  },

  iconButton: {
    width: 40,
    height: 40,
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },

  inputActive: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
  },

  label: {
    fontWeight: 500,
    fontSize: 15,
    textTransform: "capitalize",
  },

  paper: {
    borderRadius: theme.spacing(1.5),
    minWidth: theme.spacing(32),
    padding: theme.spacing(2, 0),

    "& > :not($divider)": {
      margin: theme.spacing(0, 2),
    },
  },
  header: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  badge: {
    "& .MuiBadge-badge": {
      width: theme.spacing(1.25),
      height: theme.spacing(1.25),
      minWidth: theme.spacing(1.25),
      borderRadius: "50%",
      top: theme.spacing(0.5),
      right: theme.spacing(0.5),
      border: `2px solid ${theme.palette.primary.main}`,
      padding: 0,
    },
  },
}));

export interface ActivityTypesFilterProps
  extends Omit<SelectProps, "onChange" | "value"> {
  filters: Record<ActivityType, boolean>;
  active?: boolean;
  onChange: (filters: Record<ActivityType, boolean>) => void;
}

export function ActivityTypesFilter(props: ActivityTypesFilterProps) {
  const { className, filters, onChange, active = false, ...other } = props;
  const s = useStyles();
  const ref = React.useRef<any>();
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleOpen = React.useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const handleChange: CheckboxProps["onChange"] = React.useCallback(
    (event, checked) => {
      const updatedFilters = {
        ...filters,
        [event.target.value]: checked,
      };

      const none = Object.values(updatedFilters).filter(Boolean).length === 0;

      if (!none) {
        onChange(updatedFilters);
      }
    },
    [filters, onChange],
  );

  const filterStatusText = React.useMemo(() => {
    const types = Object.values(ActivityType);
    const checkedFilters = types.filter((type) => filters[type]);
    const fullText =
      checkedFilters.length === types.length
        ? "All types"
        : checkedFilters
            .map((type) => activityResponseTypeLabels[type])
            .join(" and ") + " only";

    return `Filter by: ${fullText}`;
  }, [filters]);

  return (
    <>
      {isMobile ? (
        <IconButton
          ref={ref}
          onClick={handleOpen}
          className={clsx(s.iconButton, active && s.inputActive, className)}
        >
          <Badge
            color="primary"
            variant="dot"
            invisible={!active}
            className={s.badge}
          >
            <Filter size={24} color={theme.palette.text.primary} />
          </Badge>
        </IconButton>
      ) : (
        <Select
          ref={ref}
          open={false}
          value=""
          displayEmpty
          variant="outlined"
          onClick={handleOpen}
          className={clsx(s.select, active && s.inputActive, className)}
          renderValue={() => (
            <Typography variant="body2" className={s.label}>
              {filterStatusText}
            </Typography>
          )}
          IconComponent={ArrowDropDown}
          {...other}
        />
      )}

      <Popover
        open={open}
        anchorEl={ref.current}
        onClose={handleClose}
        classes={{ paper: s.paper }}
        anchorOrigin={{
          horizontal: isMobile ? "center" : "right",
          vertical: "bottom",
        }}
        transformOrigin={{
          horizontal: isMobile ? "center" : "right",
          vertical: "top",
        }}
      >
        <Typography className={s.header} variant="body2">
          Filter by
        </Typography>
        {Object.values(ActivityType).map((type) => (
          <FormGroup key={type}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  value={type}
                  checked={filters[type]}
                  onChange={handleChange}
                />
              }
              label={
                <Typography variant="body1" className={s.label}>
                  {activityResponseTypeLabels[type]}
                </Typography>
              }
            />
          </FormGroup>
        ))}
      </Popover>
    </>
  );
}
